// Biological Entity Emergence Probability Calculator
// Calculates the probability of biological entity emergence based on risk factors

import { ipcConfigurationService } from './ipcConfigurationService';

export class BiologicalEntityEmergenceCalculator {
  constructor() {
    this.config = ipcConfigurationService;
  }

  /**
   * Calculate emergence probability for a specific biological entity
   * @param {Object} entity - Biological entity with emergence probability factors
   * @param {Object} context - Risk assessment context
   * @returns {Object} Emergence probability result
   */
  calculateEmergenceProbability(entity, context) {
    try {
      // Step 1: Start with base probability
      let riskScore = entity.emergenceCalculation?.baseProbability || 0.05; // 5% default base
      
      // Step 2: Add patient risk factors (additive within category)
      const patientRisk = this.calculatePatientRisk(entity, context.patientRiskFactors);
      riskScore += patientRisk;
      
      // Step 3: Add environmental risk factors (additive within category)
      const environmentalRisk = this.calculateEnvironmentalRisk(entity, context.environmentalRiskFactors);
      riskScore += environmentalRisk;
      
      // Step 4: Add location risk (single value)
      const locationRisk = this.calculateLocationRisk(entity, context.locationType);
      riskScore += locationRisk;
      
      // Step 5: Apply sigmoid function to prevent >100% and reflect biological reality
      let probability = this.config.applySigmoidFunction(riskScore);
      
      // Step 6: Apply protocol compliance (multiplicative risk reduction)
      const protocolReduction = this.calculateProtocolRiskReduction(entity, context.protocols);
      probability = probability * (1 - protocolReduction);
      
      // Step 7: Apply seasonal variation
      const seasonalMultiplier = this.config.getSeasonalMultiplier(context.season);
      probability *= seasonalMultiplier;
      
      // Step 8: Apply entity priority multiplier
      const priorityMultiplier = this.config.getEntityPriorityMultiplier(entity.priority);
      probability *= priorityMultiplier;
      
      // Step 9: Cap at maximum probability
      probability = Math.min(probability, this.config.factorWeighting.maxProbability);
      
      // Step 10: Determine risk level
      const riskLevel = this.config.getRiskLevel(probability);
      
      return {
        entityId: entity.id,
        entityName: entity.commonName,
        scientificName: entity.scientificName,
        emergenceProbability: Math.round(probability * 100) / 100, // Round to 2 decimal places
        riskLevel: riskLevel,
        baseRiskScore: Math.round(riskScore * 100) / 100,
        riskBreakdown: {
          patientRisk: Math.round(patientRisk * 100) / 100,
          environmentalRisk: Math.round(environmentalRisk * 100) / 100,
          locationRisk: Math.round(locationRisk * 100) / 100,
          protocolReduction: Math.round(protocolReduction * 100) / 100,
          seasonalMultiplier: seasonalMultiplier,
          priorityMultiplier: priorityMultiplier
        },
        recommendedProtocols: this.getRecommendedProtocols(entity, probability),
        calculatedAt: new Date()
      };
      
    } catch (error) {
      console.error('Error calculating emergence probability:', error);
      return {
        entityId: entity.id,
        entityName: entity.commonName,
        emergenceProbability: 0,
        riskLevel: 'none',
        error: error.message
      };
    }
  }

  /**
   * Calculate patient risk contribution
   */
  calculatePatientRisk(entity, patientRiskFactors) {
    if (!entity.emergenceProbabilityFactors?.patientRiskFactors || !patientRiskFactors) {
      return 0;
    }

    return patientRiskFactors.reduce((totalRisk, factor) => {
      const riskIncrease = entity.emergenceProbabilityFactors.patientRiskFactors[factor.name];
      return totalRisk + (riskIncrease || 0);
    }, 0);
  }

  /**
   * Calculate environmental risk contribution
   */
  calculateEnvironmentalRisk(entity, environmentalRiskFactors) {
    if (!entity.emergenceProbabilityFactors?.environmentalRiskFactors || !environmentalRiskFactors) {
      return 0;
    }

    return environmentalRiskFactors.reduce((totalRisk, factor) => {
      const riskIncrease = entity.emergenceProbabilityFactors.environmentalRiskFactors[factor.name];
      return totalRisk + (riskIncrease || 0);
    }, 0);
  }

  /**
   * Calculate location risk contribution
   */
  calculateLocationRisk(entity, locationType) {
    if (!entity.emergenceProbabilityFactors?.locationRiskFactors || !locationType) {
      return 0;
    }

    return entity.emergenceProbabilityFactors.locationRiskFactors[locationType] || 0;
  }

  /**
   * Calculate protocol compliance risk reduction
   */
  calculateProtocolRiskReduction(entity, protocols) {
    if (!entity.emergenceProbabilityFactors?.protocolComplianceImpact || !protocols) {
      return 0;
    }

    let totalReduction = 0;
    
    protocols.forEach(protocol => {
      const protocolImpact = entity.emergenceProbabilityFactors.protocolComplianceImpact[protocol.name];
      if (protocolImpact && protocol.complianceRate) {
        // Apply compliance rate as multiplier to the impact
        const effectiveReduction = protocolImpact * protocol.complianceRate;
        totalReduction += Math.abs(effectiveReduction); // Ensure positive reduction
      }
    });

    return Math.min(totalReduction, 0.95); // Cap at 95% maximum reduction
  }

  /**
   * Get recommended protocols based on emergence probability
   */
  getRecommendedProtocols(entity, probability) {
    const recommendations = [];
    
    // Always recommend prevention focus protocols
    if (entity.preventionFocus) {
      entity.preventionFocus.forEach(protocol => {
        recommendations.push({
          protocol: protocol,
          priority: probability > 0.75 ? 'high' : probability > 0.5 ? 'medium' : 'low',
          reason: `Prevention focus for ${entity.commonName}`
        });
      });
    }

    // Add specific recommendations based on probability level
    if (probability >= this.config.getThreshold('critical')) {
      recommendations.push({
        protocol: 'Emergency Isolation Protocol',
        priority: 'critical',
        reason: 'Critical emergence probability detected'
      });
    }

    if (probability >= this.config.getThreshold('high')) {
      recommendations.push({
        protocol: 'Enhanced Monitoring Protocol',
        priority: 'high',
        reason: 'High emergence probability requires enhanced monitoring'
      });
    }

    return recommendations;
  }

  /**
   * Calculate emergence probabilities for multiple entities
   */
  calculateMultipleEmergenceProbabilities(entities, context) {
    return entities.map(entity => this.calculateEmergenceProbability(entity, context));
  }

  /**
   * Get top risk entities (above minimum threshold)
   */
  getTopRiskEntities(entities, context, limit = 10) {
    const probabilities = this.calculateMultipleEmergenceProbabilities(entities, context);
    
    return probabilities
      .filter(result => result.emergenceProbability >= this.config.getThreshold('minimumAlert'))
      .sort((a, b) => b.emergenceProbability - a.emergenceProbability)
      .slice(0, limit);
  }
}

export const biologicalEntityEmergenceCalculator = new BiologicalEntityEmergenceCalculator();

